import { IncidentInfoWrapper } from 'core/components/JianDetail';
import { EmergencyTeam } from 'core/components/JianDetail/EmergencyTeam';
import { PatientDetailSection } from './PatientDetailSection';
import { IncidentInformation } from 'core/components/JianDetail/IncidentInformation';
import { formatDateTime } from 'core/components/common/converters';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useNavigateWithParams } from 'core/hooks/useNavigateWithParams';
import { Select } from 'core/components/common/Select';
import { Tab } from 'core/components/common/Tab';
import { useTab } from 'core/components/common/Tab/useTab';
import { useIncidentDetailForUbe } from './useIncidentDetailForUbe';

export const IncidentDetail = () => {
  const { incidentInfo, emsTeamInfo, patients } = useIncidentDetailForUbe();

  const { patientId } = useParams();
  const [selectedPatient, setSelectedPatient] = useState(
    patients.find(p => p.id === patientId)
  );
  const selectablePatientList = patients.map(patient => ({
    value: patient.id,
    label: patient.baseInfo.name,
  }));

  const navigateWithParams = useNavigateWithParams();

  const onChangeSelectedPatient = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedPatientId = event.target.value;
    const selectedPatient = patients.find(
      patient => patient.id === selectedPatientId
    );

    if (selectedPatient) {
      setSelectedPatient(selectedPatient);
      navigateWithParams(
        `/jian-detail/${selectedPatient.incidentId}/${selectedPatientId}`
      );
    }
  };

  const patientTimeSeries = selectedPatient?.timeSeries;
  const timeSeries = [
    {
      title: '覚知(事案)',
      content: formatDateTime(incidentInfo.recognitionAt),
    },
    {
      title: '出動(事案)',
      content: formatDateTime(incidentInfo.departureAt),
    },
    {
      title: '現着(事案)',
      content: formatDateTime(incidentInfo.siteArrivalAt),
    },
    {
      title: '患者接触',
      content: formatDateTime(patientTimeSeries?.patientContactAt),
    },
    {
      title: '搬出開始',
      content: formatDateTime(patientTimeSeries?.extricationStartAt),
    },
    {
      title: '車収',
      content: formatDateTime(patientTimeSeries?.ambulanceAccommodationAt),
    },
    {
      title: '現発',
      content: formatDateTime(patientTimeSeries?.siteDepartureAt),
    },
    {
      title: '病着',
      content: formatDateTime(patientTimeSeries?.hospitalArrivalAt),
    },
    {
      title: '病収',
      content: formatDateTime(patientTimeSeries?.hospitalAccommodationAt),
    },
  ];

  const { selectedTabValue, handleClickTab } = useTab({
    tabs: [...UBE_PATIENT_DETAIL_TABS],
    defaultTabValue: 'baseInfo',
  });

  return (
    <>
      <IncidentInfoWrapper>
        <IncidentInformation
          incidentNo={incidentInfo.incidentNo}
          incidentType={incidentInfo.incidentType}
          telegramTargetAddress={incidentInfo.telegramTargetAddress}
          targetAddressOther={incidentInfo.telegramTargetAddress}
          timeSeries={timeSeries}
        />
        <EmergencyTeam emsTeam={emsTeamInfo} />
      </IncidentInfoWrapper>
      <div className="bg-surface pt-[60px]">
        <div className="max-w-[1280px] px-[40px] m-auto flex flex-col gap-[24px]">
          <div className="ml-auto">
            <Select
              options={selectablePatientList}
              onChange={onChangeSelectedPatient}
              selected={selectedPatient?.id || ''}
            />
          </div>
          <Tab
            tabs={[...UBE_PATIENT_DETAIL_TABS]}
            selectedTabValue={selectedTabValue}
            onClick={handleClickTab}
          />
        </div>
      </div>
      {selectedPatient && (
        <PatientDetailSection
          patient={selectedPatient}
          destinationHospitalId={incidentInfo.hospitalId}
          selectedTab={selectedTabValue}
        />
      )}
    </>
  );
};

const UBE_PATIENT_DETAIL_TABS = [
  {
    value: 'baseInfo',
    label: '基本情報',
  },
  {
    value: 'requestSummary',
    label: '要請概要',
  },
  {
    value: 'vitals',
    label: 'バイタル',
  },
  {
    value: 'findEmsContact',
    label: '接触時所見',
  },
  {
    value: 'treatment',
    label: '処置',
  },
  {
    value: 'specialTreatment',
    label: '特定行為',
  },
  {
    value: 'specialTreatmentTimeSeries',
    label: '特定行為(時系列)',
  },
  {
    value: 'photo',
    label: '写真',
  },
  {
    value: 'video',
    label: '動画',
  },
  {
    value: 'acceptRequestHistory',
    label: '要請履歴',
  },
  {
    value: 'activityRecordPdf',
    label: '活動記録PDF',
  },
  {
    value: 'prognosisInfo',
    label: '予後入力情報',
  },
] as const;

export type UbePatientDetailTabKey =
  (typeof UBE_PATIENT_DETAIL_TABS)[number]['value'];
